import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  CheckCircle, AlertTriangle, XCircle, ArrowRight, ArrowLeft,
  Mail, Building, Share2, Twitter, Linkedin, Copy, Check,
  FileText, Shield, Clock, Zap, ChevronRight
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SimulateurPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('intro'); // intro, quiz, email, result
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const questions = [
    {
      id: "status",
      question: "Quel est le statut de votre entreprise ?",
      options: [
        { value: "auto", label: "Auto-entrepreneur / Micro-entreprise", icon: "👤" },
        { value: "tpe", label: "TPE (moins de 10 salariés)", icon: "🏪" },
        { value: "pme", label: "PME (10-250 salariés)", icon: "🏢" },
        { value: "eti", label: "ETI / Grande entreprise", icon: "🏛️" }
      ]
    },
    {
      id: "clients",
      question: "À qui facturez-vous principalement ?",
      options: [
        { value: "b2c", label: "Particuliers (B2C uniquement)", icon: "👥" },
        { value: "mixed", label: "Particuliers et entreprises (mixte)", icon: "🔄" },
        { value: "b2b", label: "Entreprises uniquement (B2B)", icon: "🤝" },
        { value: "public", label: "Secteur public / Collectivités", icon: "🏛️" }
      ]
    },
    {
      id: "volume",
      question: "Combien de factures émettez-vous par mois ?",
      options: [
        { value: "low", label: "Moins de 10 factures", icon: "📄" },
        { value: "medium", label: "10 à 50 factures", icon: "📑" },
        { value: "high", label: "50 à 200 factures", icon: "📚" },
        { value: "very_high", label: "Plus de 200 factures", icon: "🗄️" }
      ]
    },
    {
      id: "current_tool",
      question: "Comment gérez-vous actuellement vos factures ?",
      options: [
        { value: "manual", label: "Excel / Word / Papier", icon: "📝" },
        { value: "basic", label: "Logiciel basique (non conforme)", icon: "💻" },
        { value: "compliant", label: "Logiciel conforme e-facturation", icon: "✅" },
        { value: "none", label: "Je ne sais pas", icon: "❓" }
      ]
    },
    {
      id: "knowledge",
      question: "Connaissez-vous Factur-X et le Portail Public de Facturation ?",
      options: [
        { value: "none", label: "Non, jamais entendu parler", icon: "🤷" },
        { value: "heard", label: "J'en ai entendu parler vaguement", icon: "👂" },
        { value: "know", label: "Je connais mais pas en détail", icon: "📖" },
        { value: "expert", label: "Oui, je maîtrise le sujet", icon: "🎓" }
      ]
    }
  ];

  const handleAnswer = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
    
    if (currentQuestion < questions.length - 1) {
      setTimeout(() => setCurrentQuestion(currentQuestion + 1), 300);
    } else {
      setStep('email');
    }
  };

  const handleSubmit = async (skipEmail = false) => {
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/simulator/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          answers: Object.entries(answers).map(([question_id, answer]) => ({
            question_id,
            answer
          })),
          email: skipEmail ? null : email,
          company_name: skipEmail ? null : companyName
        })
      });
      
      const data = await response.json();
      setResult(data);
      setStep('result');
    } catch (error) {
      console.error('Error submitting quiz:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (level) => {
    const colors = {
      excellent: { bg: 'bg-emerald-500', text: 'text-emerald-500', light: 'bg-emerald-50' },
      good: { bg: 'bg-blue-500', text: 'text-blue-500', light: 'bg-blue-50' },
      warning: { bg: 'bg-amber-500', text: 'text-amber-500', light: 'bg-amber-50' },
      critical: { bg: 'bg-red-500', text: 'text-red-500', light: 'bg-red-50' }
    };
    return colors[level] || colors.warning;
  };

  const copyShareLink = () => {
    const url = `${window.location.origin}/simulateur?result=${result?.result_id}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareOnTwitter = () => {
    const text = `J'ai obtenu ${result?.score}/100 au test de conformité e-facturation 2026 ! Et vous, êtes-vous prêt ?`;
    const url = `${window.location.origin}/simulateur`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
  };

  const shareOnLinkedIn = () => {
    const url = `${window.location.origin}/simulateur`;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
  };

  // Intro Screen
  if (step === 'intro') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900" data-testid="simulator-page">
        <div className="container mx-auto px-4 py-12">
          {/* Header */}
          <div className="text-center mb-12">
            <Link to="/" className="inline-flex items-center gap-2 text-blue-300 hover:text-blue-200 mb-8">
              <ArrowLeft className="h-4 w-4" />
              Retour à l'accueil
            </Link>
            <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 mb-4">
              Obligatoire dès septembre 2026
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Êtes-vous prêt pour<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                l'e-facturation 2026 ?
              </span>
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Testez votre niveau de préparation en 2 minutes et recevez vos recommandations personnalisées.
            </p>
          </div>

          {/* Features */}
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mb-4">
                <Clock className="h-6 w-6 text-blue-400" />
              </div>
              <h3 className="font-semibold text-white mb-2">2 minutes</h3>
              <p className="text-slate-400 text-sm">5 questions simples pour évaluer votre situation</p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-emerald-400" />
              </div>
              <h3 className="font-semibold text-white mb-2">100% gratuit</h3>
              <p className="text-slate-400 text-sm">Aucune inscription requise pour faire le test</p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <div className="w-12 h-12 rounded-xl bg-violet-500/20 flex items-center justify-center mb-4">
                <FileText className="h-6 w-6 text-violet-400" />
              </div>
              <h3 className="font-semibold text-white mb-2">Recommandations</h3>
              <p className="text-slate-400 text-sm">Un plan d'action personnalisé pour vous mettre en conformité</p>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <Button 
              onClick={() => setStep('quiz')}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-blue-500/25"
              data-testid="start-quiz-btn"
            >
              Commencer le test gratuit
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <p className="text-slate-400 text-sm mt-4">
              Test gratuit - Résultats immédiats
            </p>
          </div>

          {/* Deadline Alert */}
          <div className="max-w-2xl mx-auto mt-16 bg-amber-500/10 border border-amber-500/30 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <div className="p-2 rounded-lg bg-amber-500/20">
                <AlertTriangle className="h-6 w-6 text-amber-400" />
              </div>
              <div>
                <h3 className="font-semibold text-amber-300 mb-1">Échéances à retenir</h3>
                <ul className="text-amber-200/80 text-sm space-y-1">
                  <li>• <strong>1er septembre 2026</strong> : Réception obligatoire pour toutes les entreprises</li>
                  <li>• <strong>1er septembre 2026</strong> : Émission obligatoire pour les grandes entreprises et ETI</li>
                  <li>• <strong>1er septembre 2027</strong> : Émission obligatoire pour les PME et micro-entreprises</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Quiz Screen
  if (step === 'quiz') {
    const question = questions[currentQuestion];
    const progress = ((currentQuestion + 1) / questions.length) * 100;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl">
          {/* Progress */}
          <div className="mb-8">
            <div className="flex justify-between text-sm text-slate-400 mb-2">
              <span>Question {currentQuestion + 1} / {questions.length}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Question */}
          <Card className="bg-white border-slate-200 shadow-xl">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">
                {question.question}
              </h2>
              
              <div className="grid gap-3">
                {question.options.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleAnswer(question.id, option.value)}
                    className={`w-full p-4 rounded-xl border-2 transition-all text-left flex items-center gap-4 ${
                      answers[question.id] === option.value
                        ? 'bg-blue-500 border-blue-500 text-white'
                        : 'bg-white border-slate-200 text-slate-900 hover:bg-slate-50 hover:border-blue-300'
                    }`}
                    data-testid={`option-${option.value}`}
                  >
                    <span className="text-2xl">{option.icon}</span>
                    <span className={`font-semibold ${answers[question.id] === option.value ? 'text-white' : 'text-slate-900'}`}>{option.label}</span>
                  </button>
                ))}
              </div>

              {/* Back button */}
              {currentQuestion > 0 && (
                <button
                  onClick={() => setCurrentQuestion(currentQuestion - 1)}
                  className="mt-6 text-slate-500 hover:text-slate-700 flex items-center gap-2 mx-auto"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Question précédente
                </button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Email Screen
  if (step === 'email') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white border-slate-200 shadow-xl">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
                <Mail className="h-8 w-8 text-blue-500" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Presque terminé !
              </h2>
              <p className="text-slate-600">
                Recevez votre rapport détaillé par email
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-700 mb-2">Votre email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="vous@entreprise.com"
                    className="pl-10 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-slate-700 mb-2">Nom de l'entreprise (optionnel)</label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Ma Société SARL"
                    className="pl-10 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
                  />
                </div>
              </div>

              <Button
                onClick={() => handleSubmit(false)}
                disabled={!email || loading}
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                data-testid="submit-with-email"
              >
                {loading ? 'Analyse en cours...' : 'Voir mon score'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>

              <button
                onClick={() => handleSubmit(true)}
                disabled={loading}
                className="w-full text-slate-500 hover:text-slate-700 text-sm py-2"
                data-testid="skip-email"
              >
                Continuer sans email
              </button>
            </div>

            <p className="text-xs text-slate-500 text-center mt-6">
              Vos données restent confidentielles et ne seront jamais partagées.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Result Screen
  if (step === 'result' && result) {
    const colors = getScoreColor(result.level);

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-12 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Score Card */}
          <Card className="bg-white/10 backdrop-blur-sm border-white/20 overflow-hidden mb-8">
            <div className={`${colors.bg} p-8 text-center`}>
              <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-white/20 backdrop-blur-sm mb-4">
                <span className="text-5xl font-bold text-white" data-testid="score-value">
                  {result.score}
                </span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">{result.title}</h2>
              <p className="text-white/80 max-w-md mx-auto">{result.summary}</p>
            </div>
            
            <CardContent className="p-6">
              {/* Deadline Alert */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-amber-400" />
                  <div className="text-sm">
                    <span className="text-amber-300 font-medium">Vos échéances : </span>
                    <span className="text-amber-200/80">
                      Réception {result.deadline_info.reception} • Émission {result.deadline_info.emission}
                    </span>
                  </div>
                </div>
              </div>

              {/* Share buttons */}
              <div className="flex items-center justify-center gap-3 mb-6">
                <span className="text-slate-400 text-sm">Partager :</span>
                <button
                  onClick={shareOnTwitter}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-slate-300 hover:text-white transition-colors"
                >
                  <Twitter className="h-5 w-5" />
                </button>
                <button
                  onClick={shareOnLinkedIn}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-slate-300 hover:text-white transition-colors"
                >
                  <Linkedin className="h-5 w-5" />
                </button>
                <button
                  onClick={copyShareLink}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-slate-300 hover:text-white transition-colors"
                >
                  {copied ? <Check className="h-5 w-5 text-emerald-400" /> : <Copy className="h-5 w-5" />}
                </button>
              </div>
            </CardContent>
          </Card>

          {/* Recommendations */}
          <Card className="bg-white/10 backdrop-blur-sm border-white/20 mb-8">
            <CardContent className="p-6">
              <h3 className="text-xl font-bold text-white mb-6">Nos recommandations</h3>
              <div className="space-y-4">
                {result.recommendations.map((rec, index) => (
                  <div 
                    key={index}
                    className="bg-white/5 rounded-xl p-4 border border-white/10"
                  >
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-lg ${
                        rec.priority === 'high' ? 'bg-red-500/20' :
                        rec.priority === 'medium' ? 'bg-amber-500/20' :
                        'bg-emerald-500/20'
                      }`}>
                        {rec.priority === 'high' ? (
                          <AlertTriangle className="h-5 w-5 text-red-400" />
                        ) : rec.priority === 'medium' ? (
                          <Clock className="h-5 w-5 text-amber-400" />
                        ) : (
                          <CheckCircle className="h-5 w-5 text-emerald-400" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-white mb-1">{rec.title}</h4>
                        <p className="text-slate-400 text-sm mb-2">{rec.description}</p>
                        <span className="text-blue-400 text-sm font-medium">{rec.action} →</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Checklist */}
          <Card className="bg-white/10 backdrop-blur-sm border-white/20 mb-8">
            <CardContent className="p-6">
              <h3 className="text-xl font-bold text-white mb-6">Checklist de conformité</h3>
              <div className="space-y-3">
                {result.checklist.filter(item => item.required).map((item, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-3 p-3 rounded-lg bg-white/5"
                  >
                    {item.done ? (
                      <CheckCircle className="h-5 w-5 text-emerald-400 shrink-0" />
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-slate-500 shrink-0" />
                    )}
                    <span className={item.done ? 'text-slate-300' : 'text-white'}>
                      {item.item}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* CTA */}
          <Card className="bg-gradient-to-r from-blue-600 to-cyan-600 border-0">
            <CardContent className="p-8 text-center">
              <Zap className="h-12 w-12 text-white/80 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">
                Passez à l'action maintenant
              </h3>
              <p className="text-white/80 mb-6 max-w-md mx-auto">
                SmartFacture AI vous accompagne vers la conformité e-facturation 2026 avec un logiciel simple et certifié.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/register')}
                  className="bg-white text-blue-600 hover:bg-slate-100 px-8"
                  data-testid="cta-register"
                >
                  Essai gratuit 7 jours
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/pricing')}
                  className="border-white/30 text-white hover:bg-white/10"
                >
                  Voir les tarifs
                </Button>
              </div>
              <p className="text-white/60 text-sm mt-4">
                Sans engagement • Sans carte bancaire
              </p>
            </CardContent>
          </Card>

          {/* Restart */}
          <div className="text-center mt-8">
            <button
              onClick={() => {
                setStep('intro');
                setCurrentQuestion(0);
                setAnswers({});
                setResult(null);
              }}
              className="text-slate-400 hover:text-white text-sm"
            >
              ← Refaire le test
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default SimulateurPage;
